import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_RESOURCES,
  MN_USERS,
  MODULE,
} from '../common';
import type { Prisma, Role } from '@prisma/client';
import { RoleEnum } from '@jobcost-monorepo/database/prisma/enums';
import * as z from 'zod';
import { checkIfAdmin } from '@jobcost-monorepo/utils';
import type { KeyValue } from '@jobcost-monorepo/utils';
import UserPasswordComponent from '../ui/CustomComponents/UserPasswordComponent';

type User = Prisma.$UserPayload['scalars'];

export type IUser = User &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    resource: KeyValue;
  };

export interface IListUserFetchParams extends IListFetchParams {
  role?: Role;
}

class UserRepository extends BaseRepository<IUser, IListUserFetchParams> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['lastName', 'firstName'],
    };
    this.gridFieldsOptions = [
      {
        visible: true,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'firstName',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'lastName',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'email',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'role',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'enum',
        translationModuleName: MN_USERS,
        enumValues: RoleEnum,
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'role',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_USERS,
        translationModuleName: MN_USERS,
        enumValues: RoleEnum,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_USERS,
        translationModuleName: MN_USERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'email',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_USERS,
        translationModuleName: MN_USERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'resourceId',
        linkedFieldForDefaultValue: 'resource.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_USERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'lastName',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_USERS,
        translationModuleName: MN_USERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 1 },
        linkedField: 'firstName',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_USERS,
        translationModuleName: MN_USERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'phone',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_USERS,
        translationModuleName: MN_USERS,
      },
      {
        visible: true,
        readOnly: true,
        position: { x: 1, y: 2 },
        linkedField: 'hasDarkTheme',
        type: 'string',
        width: 'w-full',
        formatType: 'boolean',
        moduleName: MN_USERS,
        translationModuleName: MN_USERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 2 },
        linkedField: 'role',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdown',
        moduleName: MN_USERS,
        translationModuleName: MN_USERS,
        enumValues: RoleEnum,
        checkIfDisabled: () => !checkIfAdmin(),
      },
      {
        visible: true,
        readOnly: true,
        position: { x: 0, y: 3 },
        linkedField: 'passwordComponent',
        formatType: 'custom',
        customComponent: UserPasswordComponent,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      uid: z.string().nullable().optional(),
      email: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .email({
          message: 'invalidEmail',
        }),
      resourceId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      lastName: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      firstName: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      phone: z
        .string()
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' })
        .nullable()
        .optional(),
      hasDarkTheme: z.boolean().nullable().optional(),
      role: z.string().nullable().optional(),
      password: z.string().nullable().optional(),
      passwordConfirm: z.string().nullable().optional(),
    });
  }
}

export default UserRepository;
